import { useCallback, useEffect } from "react";
import { useAuthState, useSignInWithEmailAndPassword, useSignOut, useCreateUserWithEmailAndPassword } from "react-firebase-hooks/auth";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../config/firebase";
import intercomService from "../../services/intercom";

// Debug logging function
const debug = (message, data) => {
  // console.log(`[Auth Debug] ${message}`, data);
};

export const useAuth = () => {
  const [user, authLoading, error] = useAuthState(auth);
  const staffDocRef = user ? doc(db, "staff", user.uid) : null;
  const [staffProfile, staffLoading, staffError] = useDocumentData(staffDocRef);

  // Debug logging for admin invite button issue
  // console.log('[Auth Debug] Current state:', {
  //   user: user ? user.uid : null,
  //   userEmail: user ? user.email : null,
  //   staffProfile: staffProfile ? {
  //     role: staffProfile.role,
  //     email: staffProfile.email,
  //     displayName: staffProfile.displayName
  //   } : null,
  //   isAuthenticated: !!user && !!staffProfile,
  //   authLoading,
  //   staffLoading,
  //   staffError: staffError ? staffError.message : null
  // });

  const [signInWithEmail, , signInLoading, signInError] = useSignInWithEmailAndPassword(auth);
  const [signOut, signOutLoading, signOutError] = useSignOut(auth);
  const [createUserWithEmailAndPassword, , createLoading, createError] = useCreateUserWithEmailAndPassword(auth);

  // Consider auth as loading if either Firebase auth or staff profile is loading
  // But only if we have a user and are waiting for their profile
  const isAuthLoading = authLoading || (user && staffLoading);

  const signIn = useCallback(
    async (email, password) => {
      try {
        const result = await signInWithEmail(email, password);

        if (!result) {
          debug("Sign in failed - no result");
          return {
            success: false,
            error: "Authentication failed. Please check your credentials and try again.",
          };
        }

        debug("Sign in successful", { uid: result.user.uid });
        return { success: true, user: result.user };
      } catch (err) {
        debug("Sign in error", err);
        let errorMessage = "Authentication failed";

        if (err.code === "auth/operation-not-allowed") {
          errorMessage = "Email/Password authentication is not enabled.";
        } else if (err.code === "auth/user-disabled") {
          errorMessage = "This account has been disabled.";
        } else if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password") {
          errorMessage = "Invalid email or password.";
        }

        return {
          success: false,
          error: errorMessage,
          code: err.code,
        };
      }
    },
    [signInWithEmail]
  );

  const createUser = useCallback(
    async (email, password, displayName = "", phoneNumber = "", department = "", jobTitle = "") => {
      try {
        const result = await createUserWithEmailAndPassword(email, password);

        if (!result) {
          debug("Create user failed - no result");
          return {
            success: false,
            error: "Failed to create account. Please try again.",
          };
        }

        try {
          const now = serverTimestamp();

          // Create a more comprehensive staff data object
          const staffData = {
            // Basic profile information
            email: result.user.email,
            displayName: displayName || "",
            phoneNumber: phoneNumber || "",
            department: department || "",
            jobTitle: jobTitle || "",
            photoURL: "",

            // Status and activity tracking
            status: "online",
            isOnline: true,
            statusUpdatedAt: now,
            lastLogin: now,
            lastActive: now,

            // Password reset fields
            passwordResetRequested: false,
            passwordResetTimestamp: null,

            // Permissions
            role: "staff",

            // Audit tracking
            createdAt: now,
            updatedAt: now,
          };

          await setDoc(doc(db, "staff", result.user.uid), staffData);
        } catch (firestoreErr) {
          debug("Error creating staff document", firestoreErr);
          return {
            success: false,
            error: "Account created but failed to set up profile. Error: " + firestoreErr.message,
          };
        }

        return { success: true, user: result.user };
      } catch (err) {
        debug("Create user error", err);
        let errorMessage = "Registration failed";

        if (err.code === "auth/operation-not-allowed") {
          errorMessage = "Email/Password authentication is not enabled.";
        } else if (err.code === "auth/email-already-in-use") {
          errorMessage = "This email is already registered.";
        } else if (err.code === "auth/invalid-email") {
          errorMessage = "Invalid email address format.";
        } else if (err.code === "auth/weak-password") {
          errorMessage = "Password is too weak. Please use a stronger password.";
        }

        return {
          success: false,
          error: errorMessage,
          code: err.code,
        };
      }
    },
    [createUserWithEmailAndPassword]
  );

  const logout = useCallback(async () => {
    try {
      // Sign out from Firebase
      await signOut();
      return { success: true };
    } catch (err) {
      debug("Logout error", err);
      return {
        success: false,
        error: err.message || "Sign out failed",
      };
    }
  }, [signOut]);

  // Request password reset function
  const requestPasswordReset = useCallback(async () => {
    if (!user || !staffProfile) {
      debug("No user or staff profile found for password reset");
      return {
        success: false,
        error: "You must be logged in to request a password reset",
      };
    }

    try {
      // Update staff document to mark password reset as requested
      await setDoc(
        doc(db, "staff", user.uid),
        {
          passwordResetRequested: true,
          passwordResetTimestamp: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      return { success: true };
    } catch (err) {
      debug("Password reset request error", err);
      return {
        success: false,
        error: err.message || "Failed to request password reset",
      };
    }
  }, [user, staffProfile]);

  // Admin function to reset password
  const resetPassword = useCallback(async (email) => {
    try {
      const { sendPasswordResetEmail } = await import("firebase/auth");
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (err) {
      debug("Password reset error", err);
      let errorMessage = "Failed to send password reset email";

      if (err.code === "auth/user-not-found") {
        errorMessage = "No user found with this email address.";
      } else if (err.code === "auth/invalid-email") {
        errorMessage = "Invalid email address format.";
      }

      return {
        success: false,
        error: errorMessage,
        code: err.code,
      };
    }
  }, []);

  // Update user profile data
  const updateProfile = useCallback(
    async (profileData) => {
      if (!user) {
        return {
          success: false,
          error: "You must be logged in to update your profile",
        };
      }

      try {
        await setDoc(
          doc(db, "staff", user.uid),
          {
            ...profileData,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        return { success: true };
      } catch (err) {
        debug("Profile update error", err);
        return {
          success: false,
          error: err.message || "Failed to update profile",
        };
      }
    },
    [user]
  );

  // Update last active time
  const updateLastActive = useCallback(async () => {
    if (!user) return;

    try {
      await setDoc(
        doc(db, "staff", user.uid),
        {
          lastActive: serverTimestamp(),
          isOnline: true,
          status: "online",
        },
        { merge: true }
      );
    } catch (err) {
      debug("Last active update error", err);
    }
  }, [user]);

  // Initialize and manage Intercom based on auth state
  useEffect(() => {
    if (user && staffProfile) {
      // User is authenticated with staff profile
      console.log('[Auth] Operator authenticated:', user.email);
      // Note: We don't initialize the Intercom widget for operators
      // They should use the Customer Support page to access Intercom Inbox
      
      // Store user reference in service for potential API usage
      intercomService.boot(user, staffProfile);
    }
  }, [user, staffProfile]);

  return {
    user,
    staffProfile,
    isAuthenticated: !!user && !!staffProfile,
    isLoading: isAuthLoading || signInLoading || signOutLoading || createLoading,
    error: error || staffError || signInError || signOutError || createError,
    signIn,
    logout,
    createUser,
    resetPassword,
    requestPasswordReset,
    updateProfile,
    updateLastActive,
    currentUser: user,
  };
};

export default useAuth;
